// components/shared/HardNavLink.tsx
// Fallback navigation cho bug Next.js stuck (desktop production).
// - Route SPA-friendly: thử router.push, hard nav nếu stuck sau 400ms.
// - Route đã biết stuck: hard nav ngay sau 80ms (tiết kiệm thời gian flash).
// Progress bar top giống YouTube để che flash trắng.
"use client";

import type { ReactNode, MouseEvent, AnchorHTMLAttributes } from "react";
import { useRouter, usePathname } from "next/navigation";

interface Props extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick"> {
  href: string;
  children?: ReactNode;
  onClick?: () => void;
  /** Thời gian chờ router.push trước khi fallback hard nav (ms). Default 400. */
  fallbackMs?: number;
}

// ── Danh sách route đã verify stuck SPA trên production desktop ──
// Các route này skip router.push, hard navigate ngay để đỡ flash trắng lâu.
const STUCK_ROUTE_PREFIXES = [
  "/paths",
  "/reviews",
  "/studio",
  "/invite",
  "/admin",
  "/my-paths",
  "/settings",
  "/wallet",
  "/badges",
  "/help",
  "/notifications",
  "/orgs",
  "/positions",
  "/interview",
];

function isStuckRoute(path: string): boolean {
  return STUCK_ROUTE_PREFIXES.some(p => path === p || path.startsWith(p + "/"));
}

export default function HardNavLink({
  href,
  children,
  onClick,
  fallbackMs = 400,
  ...rest
}: Props) {
  const router = useRouter();
  const pathname = usePathname();

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    // Tôn trọng modifier keys + middle click - cho browser xử lý tab mới
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;

    e.preventDefault();
    onClick?.();

    const targetPath = href.split("?")[0].split("#")[0];
    if (pathname === targetPath) return;

    // Progress bar hiện ngay để user thấy phản hồi
    showNavBar();

    // Route đã verify SPA stuck → hard nav ngay, không đợi router.push
    if (isStuckRoute(targetPath)) {
      window.setTimeout(() => { window.location.href = href; }, 80);
      return;
    }

    // Route SPA-friendly → thử router.push trước
    router.push(href);

    // Fallback: nếu sau fallbackMs pathname vẫn chưa đổi → hard navigate
    window.setTimeout(() => {
      if (window.location.pathname !== targetPath) {
        window.location.href = href;
      } else {
        finishNavBar();
      }
    }, fallbackMs);
  }

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}

// ── Progress bar singleton (vanilla DOM, style YouTube xanh dương) ──
function showNavBar() {
  if (typeof document === "undefined") return;

  const existing = document.getElementById("hardnav-bar");
  if (existing) existing.remove();

  const el = document.createElement("div");
  el.id = "hardnav-bar";
  el.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "height:3px",
    "width:0",
    "background:linear-gradient(90deg,#2563eb,#3b82f6,#60a5fa)",
    "box-shadow:0 0 10px rgba(59,130,246,.7)",
    "z-index:99999",
    "transition:width 300ms cubic-bezier(.4,0,.2,1)",
    "pointer-events:none",
    "border-radius:0 2px 2px 0",
  ].join(";");
  document.body.appendChild(el);

  // 0 → 70% nhanh, dừng đợi navigation xong
  requestAnimationFrame(() => {
    el.style.width = "70%";
  });
}

function finishNavBar() {
  const el = document.getElementById("hardnav-bar");
  if (!el) return;
  el.style.transition = "width 200ms ease-out, opacity 250ms ease-out 200ms";
  el.style.width = "100%";
  el.style.opacity = "0";
  setTimeout(() => el.remove(), 500);
}

/** Điều hướng cứng dùng cho onClick/handler không phải thẻ <a>. */
export function hardNavigate(href: string) {
  if (typeof window === "undefined") return;
  const targetPath = href.split("?")[0].split("#")[0];
  if (window.location.pathname === targetPath) return;
  showNavBar();
  window.setTimeout(() => { window.location.href = href; }, 80);
}